import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CreateOrderInput,
  isPaymentRequestId,
  OrderStatus,
  PaymentApi,
  paymentApi,
  PaymentApiError,
  PaymentOrder,
  ReleaseReason,
} from '../lib/paymentApi';
import { BrowserRazorpayCheckout, CheckoutOutcomeKind, PaymentCheckout } from '../lib/razorpayCheckout';

export enum CheckoutStateKind {
  Idle = 'idle',
  Creating = 'creating',
  Checkout = 'checkout',
  Verifying = 'verifying',
  Pending = 'pending',
  Success = 'success',
  Error = 'error',
}
export type PaymentCheckoutState =
  | { readonly kind: CheckoutStateKind.Idle }
  | { readonly kind: CheckoutStateKind.Creating; readonly requestId: string }
  | {
      readonly kind: CheckoutStateKind.Checkout | CheckoutStateKind.Verifying;
      readonly order: PaymentOrder;
    }
  | {
      readonly kind: CheckoutStateKind.Pending;
      readonly requestId: string;
      readonly message: string;
    }
  | { readonly kind: CheckoutStateKind.Success; readonly order: PaymentOrder }
  | { readonly kind: CheckoutStateKind.Error; readonly message: string };
const storageKey = 'rt-payment-pending';
function message(error: unknown): string {
  return error instanceof Error ? error.message : 'Payment could not be confirmed. Please check its status.';
}
function assertNever(value: never): never {
  throw new Error('Unhandled checkout outcome');
}
export function usePaymentCheckout(
  userId: string | undefined,
  api: PaymentApi = paymentApi,
  injectedCheckout?: PaymentCheckout,
) {
  const [browser] = useState(() => new BrowserRazorpayCheckout());
  const checkout = injectedCheckout ?? browser;
  const [state, setState] = useState<PaymentCheckoutState>({
    kind: CheckoutStateKind.Idle,
  });
  const [retryGeneration, setRetryGeneration] = useState(0);
  const locked = useRef(false);
  const epoch = useRef(0);
  const invalidate = useCallback(() => {
    epoch.current += 1;
  }, []);
  const pendingId = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedUser = useRef(userId);
  useEffect(() => {
    savedUser.current = userId;
  }, [userId]);
  const current = (version: number) => epoch.current === version;
  const remember = useCallback((id: string | null) => {
    if (id) sessionStorage.setItem(storageKey, JSON.stringify({ userId: savedUser.current, requestId: id }));
    else {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* Captured status remains authoritative if storage is unavailable. */
      }
    }
    pendingId.current = id;
  }, []);
  const pending = useCallback(
    (id: string, text = 'Payment confirmation pending. Check status before making another payment.') => {
      setState({
        kind: CheckoutStateKind.Pending,
        requestId: id,
        message: text,
      });
    },
    [],
  );
  const apply = useCallback(
    (order: PaymentOrder): boolean => {
      if (order.status === OrderStatus.Captured) {
        remember(null);
        setState({ kind: CheckoutStateKind.Success, order });
        return true;
      }
      // The backend closed this attempt after checking that Razorpay has nothing
      // captured, authorized or still processing for it. Razorpay wants a new order for
      // every new attempt, so the next checkout starts fresh; a late capture of the old
      // attempt is refunded by the backend.
      if (order.recovery_disposition === 'retry_allowed') {
        if (timer.current) clearTimeout(timer.current);
        remember(null);
        setState({ kind: CheckoutStateKind.Idle });
        setRetryGeneration((v) => v + 1);
        return true;
      }
      pending(order.id);
      return false;
    },
    [pending, remember],
  );
  const checkStatus = useCallback(async () => {
    const id = pendingId.current;
    if (!id || locked.current) return;
    locked.current = true;
    const version = epoch.current;
    try {
      const order = await api.reconcile(id);
      if (current(version)) apply(order);
    } catch (error: unknown) {
      if (current(version)) pending(id, message(error));
    } finally {
      if (current(version)) locked.current = false;
    }
  }, [api, apply, pending]);

  useEffect(() => {
    const version = ++epoch.current;
    locked.current = false;
    pendingId.current = null;
    setState({ kind: CheckoutStateKind.Idle });
    if (userId) {
      try {
        const raw: unknown = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null');
        if (
          typeof raw === 'object' &&
          raw !== null &&
          'userId' in raw &&
          raw.userId === userId &&
          'requestId' in raw &&
          isPaymentRequestId(raw.requestId)
        ) {
          const id = raw.requestId;
          pendingId.current = id;
          pending(id);
          void api
            .get(id)
            .then((order) => {
              if (!current(version)) return;
              if (order.status === OrderStatus.Captured || order.recovery_disposition === 'retry_allowed')
                apply(order);
              else void checkStatus();
            })
            .catch((error: unknown) => {
              if (!current(version)) return;
              if (error instanceof PaymentApiError && error.status === 404) {
                // A timed-out creation may still be executing; retain the recovery ID.
                pending(id, 'Checkout has not been found yet. Check again before releasing this attempt.');
              } else pending(id, message(error));
            });
        }
      } catch {
        setState({
          kind: CheckoutStateKind.Error,
          message: 'Payment recovery storage is unavailable.',
        });
      }
    }
    return () => {
      invalidate();
      if (timer.current) clearTimeout(timer.current);
      checkout.close();
    };
  }, [userId, api, checkout, apply, checkStatus, pending, invalidate]);

  const poll = async (id: string, version: number, deadline: number): Promise<void> => {
    if (!current(version)) return;
    try {
      const order = await api.reconcile(id);
      if (!current(version) || apply(order)) return;
    } catch (error: unknown) {
      if (current(version)) pending(id, message(error));
      return;
    }
    if (current(version) && Date.now() < deadline) {
      timer.current = setTimeout(() => {
        void poll(id, version, deadline);
      }, 2000);
    }
  };

  const start = async (input: Omit<CreateOrderInput, 'request_id'>): Promise<PaymentOrder | null> => {
    if (locked.current || pendingId.current || !userId) return null;
    locked.current = true;
    const version = ++epoch.current;
    let id: string | null = null;
    let created: PaymentOrder;
    try {
      // The server owns the Razorpay mode and public Key ID, so switching test to live or
      // rotating keys never needs a frontend rebuild. The key must belong to that mode.
      const config = await api.config();
      const key = config.key_id;
      if (!config.enabled || !key.startsWith(`rzp_${config.payment_mode}_`))
        throw new Error('Payments are not configured.');
      const nextId = crypto.randomUUID();
      setState({ kind: CheckoutStateKind.Creating, requestId: nextId });
      await checkout.load();
      if (!current(version)) return null;
      remember(nextId);
      id = nextId;
      setState({ kind: CheckoutStateKind.Creating, requestId: id });
      created = await api.create({ ...input, request_id: id });
      if (!current(version)) return null;
      if (created.id !== id) throw new Error('Checkout response does not match this attempt.');
      // Never open a live Checkout on a test order, or the other way round.
      if (created.payment_mode !== config.payment_mode)
        throw new Error('Payment mode changed; please retry.');
      setState({ kind: CheckoutStateKind.Checkout, order: created });
      const outcome = await checkout.open(created, key);
      if (!current(version)) return null;
      switch (outcome.kind) {
        case CheckoutOutcomeKind.Success: {
          setState({ kind: CheckoutStateKind.Verifying, order: created });
          const order = await api.verify(outcome.proof);
          if (!current(version)) return null;
          if (order.id !== id) throw new Error('Payment response does not match this attempt.');
          if (apply(order)) return order;
          void poll(id, version, Date.now() + 30000);
          return null;
        }
        case CheckoutOutcomeKind.Dismissed:
        case CheckoutOutcomeKind.Failed: {
          const order = await api.cancel(
            id,
            outcome.kind === CheckoutOutcomeKind.Failed
              ? ReleaseReason.PaymentFailed
              : ReleaseReason.Cancelled,
          );
          if (!current(version)) return null;
          if (apply(order)) return order.status === OrderStatus.Captured ? order : null;
          pending(
            id,
            outcome.kind === CheckoutOutcomeKind.Failed
              ? outcome.message
              : 'Payment window closed. Check status before retrying.',
          );
          return null;
        }
        default:
          return assertNever(outcome);
      }
    } catch (error: unknown) {
      if (!current(version)) return null;
      if (id) pending(id, message(error));
      else setState({ kind: CheckoutStateKind.Error, message: message(error) });
      return null;
    } finally {
      if (current(version)) locked.current = false;
    }
  };
  const releaseAndRetry = async () => {
    const id = pendingId.current;
    if (!id || locked.current) return;
    locked.current = true;
    const version = epoch.current;
    try {
      const confirmed = await api.reconcile(id);
      if (!current(version) || (confirmed.status === OrderStatus.Captured && apply(confirmed))) return;
      const order = await api.cancel(id);
      if (!current(version)) return;
      apply(order);
    } catch (error: unknown) {
      if (current(version)) pending(id, message(error));
    } finally {
      if (current(version)) locked.current = false;
    }
  };
  const busy =
    state.kind === CheckoutStateKind.Creating ||
    state.kind === CheckoutStateKind.Checkout ||
    state.kind === CheckoutStateKind.Verifying;
  return {
    state,
    retryGeneration,
    start,
    checkStatus,
    releaseAndRetry,
    blocked: busy || state.kind === CheckoutStateKind.Pending,
  };
}
